import React, { useEffect, useState } from 'react';
import { Resolver, useForm } from 'react-hook-form';
import { yupResolver } from '@hookform/resolvers/yup';
import { useTranslations } from 'hooks/useTranslations';
import { StyledPageTitle } from 'theme/styles';
import Breadcrumbs from 'components/atoms/Breadcrumbs';
import { ERouteLinks } from 'models/route';
import { useNavigate, useParams } from 'react-router-dom';
import { Loader } from 'components/atoms/Loader';
import { ILicenceAuthorityPrintoutSettingsFormValues } from 'models/licenceAuthority';
import { useLicenceAuthorities } from '../hooks';
import LicenceAuthoritiesPrintoutSettingsForm from '../LicenceAuthoritiesPrintoutSettingsForm';
import Grid from '@mui/material/Grid';
import { Alert, EAlertVariants } from '../../../components/atoms/Alert';
import { LegalEntityDetails } from '../../../components/organisms/LegalEntityDetails';
import { EDetailsPageTypes } from '../../../components/templates/DetailsTemplate';
import { StyledValueDetails } from '../styles';
import { Typography } from '@mui/material';
import { emptyFieldHolder } from '../../../components/molecules/CardList';
import Button from '../../../components/atoms/Button';
import { EButtonVariants } from '../../../constants/Buttons';
import { formatSendHour, getSendFrequencyLabel } from '../helpers';

const LicenceAuthoritiesPrintoutSettingsDelete = () => {
  const { t } = useTranslations();
  const navigate = useNavigate();
  const { id = '' } = useParams<{ id: string }>();
  const [isSubmitLoading, setIsSubmitLoading] = useState(false);
  const [isLoading, setIsLoading] = useState(true);
  const [initialFormValues, setInitialFormValues] =
    useState<ILicenceAuthorityPrintoutSettingsFormValues>();
  const {
    printoutSettings,
    getPrintoutSettings,
    deletePrintoutSettings,
    printoutDeletionInfo,
    readPrintoutDeletionInfo,
  } = useLicenceAuthorities();

  useEffect(() => {
    (async function init() {
      setIsLoading(true);
      getPrintoutSettings(id);
      readPrintoutDeletionInfo(id);
      setIsLoading(false);
    })();
  }, [id]);

  useEffect(() => {
    if (!isLoading && printoutSettings) {
      setInitialFormValues({
        username: printoutSettings.username,
        host: printoutSettings.host,
        port: printoutSettings.port,
        hostFingerprints: printoutSettings.hostFingerprints,
        pgpPublicKey: printoutSettings.pgpForeignPublicKey,
        outboundPath: printoutSettings.outboundPath,
        inboundPath: printoutSettings.inboundPath,
        sendHours: printoutSettings.sendHours,
      });
    }
  }, [isLoading, printoutSettings]);

  const onSubmit = async () => {
    setIsSubmitLoading(true);
    await deletePrintoutSettings(id, printoutSettings?.version ?? 0);
    setIsSubmitLoading(false);
    navigate(ERouteLinks.DetailsLicenceAuthorities.replace(':id', id));
  };

  const onCancel = () =>
    navigate(ERouteLinks.DetailsLicenceAuthoritiesPrintoutSettings.replace(':id', id));

  return (
    <>
      <Breadcrumbs
        items={[
          { label: t('module.administration.name'), route: ERouteLinks.Administration },
          { label: t('licenceAuthorities.header'), route: ERouteLinks.LicenceAuthorities },
          {
            label: t('licenceAuthorities.detailsLicenceAuthority.header'),
            route: ERouteLinks.DetailsLicenceAuthorities.replace(':id', id),
          },
          {
            label: t('licenceAuthorities.printoutSettingsDetails.header'),
            route: ERouteLinks.DetailsLicenceAuthoritiesPrintoutSettings.replace(':id', id),
          },
          t('licenceAuthorities.deletePrintoutSettings.header'),
        ]}
      />
      <StyledPageTitle variant="h4">
        {t('licenceAuthorities.deletePrintoutSettings.header')}
      </StyledPageTitle>
      <Grid size={{ xs: 12 }} style={{ margin: '10px 0 10px' }}>
        <Alert
          text={
            printoutDeletionInfo?.hasWaitingPrintRequests
              ? t('deletePrintoutSettings.contains.requests.info.text')
              : t('deletePrintoutSettings.info.text')
          }
          variant={EAlertVariants.warning}
        />
      </Grid>
      {isLoading ? (
        <Loader isLoading={true} />
      ) : (
        <StyledValueDetails container size={{ xs: 12 }} columnSpacing="10px">
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.username.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.username)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.host.label')}
            </Typography>
            <Typography variant="body2">{emptyFieldHolder(printoutSettings?.host)}</Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.port.label')}
            </Typography>
            <Typography variant="body2">{emptyFieldHolder(printoutSettings?.port)}</Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.fingerprints.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.hostFingerprints.join(', '))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.outboundPath.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.outboundPath)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.inboudPath.label')}
            </Typography>
            <Typography variant="body2">
              {emptyFieldHolder(printoutSettings?.inboundPath)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.keyPrintoutSystem.label')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {emptyFieldHolder(printoutSettings?.pgpForeignPublicKey)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.keyFirecycleSystem.label')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {emptyFieldHolder(printoutSettings?.pgpOwnPublicKey)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.publicKey.label')}
            </Typography>
            <Typography
              variant="body2"
              sx={{ whiteSpace: 'pre-wrap', wordBreak: 'break-all' }}
            >
              {emptyFieldHolder(printoutSettings?.sshOwnPublicKey)}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.sendFrequency.label')}
            </Typography>

            <Typography variant="body2">
              {emptyFieldHolder(getSendFrequencyLabel(printoutSettings?.sendHours, t))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.firstPrintingHours.label')}
            </Typography>

            <Typography variant="body2">
              {emptyFieldHolder(formatSendHour(printoutSettings?.sendHours[0]))}
            </Typography>
          </Grid>
          <Grid size={{ xs: 12, md: 4 }} sx={{ p: 2 }}>
            <Typography variant="subtitle2" sx={{ marginBottom: 0.5 }}>
              {t('licenceAuthoritiesPrintoutSettings.secondPrintingHours.label')}
            </Typography>

            <Typography variant="body2">
              {emptyFieldHolder(formatSendHour(printoutSettings?.sendHours[1]))}
            </Typography>
          </Grid>
        </StyledValueDetails>
      )}
      <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
        <Grid container columnSpacing={3} size={{ xs: 12 }} sx={{ rowGap: 3, mt: 2 }}>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Button
              fullWidth
              label={t('general.yes.button')}
              id="yes-button"
              variant={EButtonVariants.contained}
              isLoading={isLoading}
              onClick={onSubmit}
            />
          </Grid>
          <Grid size={{ xs: 12, lg: 4 }}>
            <Button
              fullWidth
              label={t('general.no.button')}
              id="no-button"
              variant={EButtonVariants.outlined}
              onClick={onCancel}
            />
          </Grid>
        </Grid>
      </Grid>
    </>
  );
};

export default LicenceAuthoritiesPrintoutSettingsDelete;
